import { useMemo } from "react";
import type { z } from "zod";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  createInitialValues,
  getWorkflowTranslationContext,
  tk,
  workflows,
} from "@thalamos/vision-common";

import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type FinishFormSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["finishForm"]["formDefinition"]["sections"][0]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function FinishFormSkeleton() {
  return (
    <MultiPageFormSkeleton headerText={tk("finishForm.leavePlaceOfSafety")} />
  );
}

///

export type FinishFormProps<T extends WorkflowVersion> = {
  onSubmit: (value: FinishFormSchema<T>) => Promise<void>;
  onCancel: () => void;
  workflowVersion: T;
  initialValues?: FinishFormSchema<T>;
  incidentWorkItem?: IncidentWorkItemWithContext;
};

///

export function FinishForm<T extends WorkflowVersion>(
  props: FinishFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const formSpecification =
    workflow?.finishForm.formDefinition.sections[0]?.buildFormSpecification({
      incidentWorkItem: props.incidentWorkItem,
    });

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(
    () =>
      formSpecification === undefined
        ? {}
        : createInitialValues(
            formSpecification.strictFieldSchemas,
            props.initialValues,
          ),
    [props.initialValues, formSpecification],
  );

  if (formSpecification === undefined) {
    return <Error />;
  }

  const formSchema = formSpecification.formSchema;
  const strictFieldSchemas = formSpecification.strictFieldSchemas;

  return (
    <MultiPageForm<typeof formSchema>
      headerText={({ incidentWorkItem }) => ({
        translationKey: tk("finishForm.leavePlaceOfSafety"),
        translationOptions: {
          context: getWorkflowTranslationContext({ incidentWorkItem }),
        },
      })}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
    />
  );
}
